import { message } from 'antd';
import IStep from '../../lib/IStep';
import Api from '../../services/Api';
import BusinessModel, { IBusiness } from './models/BusinessModel';
import StepperModel from '../../models/StepperModel';
import i18n from 'i18next';
import CategoryKeywordModel from '../CategoryKeywords/models/CategoryKeywordModel';
import { STEP_CATEGORY_KEYWORDS } from '../steps-definitions';
import DesignModel from '../Design/models/DesignModel';
import BusinessDescriptionModel from '../BusinessDescription/models/BusinessDescriptionModel';
import SiteMapModel from '../Sitemap/models/SiteMapModel';
import { normalizeWebsiteForSubmission } from '../../helpers/domain-url/DomainUrlValidation';

export default class Step implements IStep {
  /**
   * Fired when the step is initialized
   */
  onInit() {
    // run business logic here when the step is initialized
  }

  public async selectBusiness(
    business: IBusiness,
    website: string,
    domain_type: string
  ) {
    BusinessModel.setSelectedCategory(null);
    BusinessModel.setSelectedBusinessWebsite(website);

    if (website) {
      BusinessModel.setSelectedBusinessDomainType('user_has_existing_website_domain');
    } else {
      BusinessModel.setSelectedBusinessDomainType(domain_type);
    }

    if (BusinessModel.businessSearchSource === 'gmb') {
      const categoryFromScraper =
        (business as any).category ||
        (business as any).primaryTypeDisplayName ||
        (business as any).primaryType ||
        null;
      if (categoryFromScraper) {
        BusinessModel.setSelectedCategory(categoryFromScraper);
      }
      BusinessModel.setSelectedBusinessId(business.id);
      return; 
    }

    try {
      const result = await Api.scrapeGooglePlaces(business.id);

      if (
        result.success === false ||
        result.category === null ||
        result.category === ''
      ) {
        BusinessModel.setSelectedBusinessId(business.id);
        return;
      }

      BusinessModel.setSelectedCategory(result.category);
      BusinessModel.setSelectedBusinessId(business.id);
    } catch (error) {
      BusinessModel.setSelectedBusinessId(business.id);
      console.error(error);
    }
  }

  public async moveToNextStep() {
    BusinessModel.setMovingToNextStepLoading(true);

    try {
      const selectedBusiness = BusinessModel.getSelectedBusiness();
      const manualData = BusinessModel.manualCompanyData;
      const domainType = BusinessModel.selectedBusinessDomainType;

      let businessData: any;
      if (selectedBusiness) {
        businessData = {
          company_name: selectedBusiness.name,
          zip: selectedBusiness.zipCode,
          address: selectedBusiness.address,
          street: selectedBusiness.street,
          state: selectedBusiness.state,
          city: selectedBusiness.city,
          country: selectedBusiness.country,
          phone: selectedBusiness.phoneNumber,
          website: selectedBusiness.website,
          domain_type: domainType,
          category: BusinessModel.selectedCategoryName,
          google_place_id: selectedBusiness.id,
          openingHours: selectedBusiness.openingHours,
          openingHoursFormatted: selectedBusiness.openingHoursFormatted
        };
        
      } else if (manualData) {
        businessData = {
          ...manualData,
          domain_type: domainType,
          category: BusinessModel.selectedCategoryName
        };
      } else {
        throw new Error('No business data available');
      }

      if (businessData.website && domainType) {
        const normalizedMode =
          domainType === 'user_wants_website_domain'
            ? 'user_wants_website_domain'
            : 'user_has_existing_website_domain';
        const normalizedResult = normalizeWebsiteForSubmission({
          value: businessData.website,
          mode: normalizedMode
        });

        if (!normalizedResult.isValid || !normalizedResult.normalizedForSubmit) {
          throw new Error(
            normalizedResult.errorKey || 'Invalid URL format.'
          );
        }

        businessData.website = normalizedResult.normalizedForSubmit;
      }

      BusinessModel.businessWizardData.setFormData(businessData);

      const backgroundTasks: Promise<any>[] = [];

      if (domainType !== 'user_wants_website_domain') {
        backgroundTasks.push(
          DesignModel.extractWebsiteLogo(
            businessData.website,
            businessData.category
          ).catch(() => {})
        );
      }

      backgroundTasks.push(
        Api.generateBusinessDescription({
          category: businessData.category,
          companyName: businessData.company_name,
          city: businessData.city,
          website: businessData.website
        })
          .then((businessDescription) => {
            if (businessDescription.status === 'success') {
              BusinessDescriptionModel.businessDescriptionWizardData.setFormData({
                business_description:
                  businessDescription.data.businessDescription
              });
            }
          })
          .catch(() => {})
      );

      if (DesignModel.colorThemeStateMachine.colorSchema === null) {
        backgroundTasks.push(
          Api.generateColors(businessData.category)
            .then((colorData) => {
              DesignModel.colorThemeStateMachine.transition(
                'setInitialColorSchema',
                { colorSchema: colorData.data.colors }
              );
            })
            .catch(() => {})
        );
      }

      if (domainType !== 'user_wants_website_domain') {
        backgroundTasks.push(
          SiteMapModel.fetchExternalSiteMap(
            businessData.website,
            businessData.company_name,
            businessData.country
          ).catch(() => {})
        );
      }

  Promise.allSettled(backgroundTasks);

      await CategoryKeywordModel.fetchCategories(businessData.category);

      BusinessModel.setStartLoadingCategoryDataForId(null);
      BusinessModel.setMovingToNextStepLoading(false);
      StepperModel.changeStep(STEP_CATEGORY_KEYWORDS);
      await CategoryKeywordModel.moveToNextStep();
    } catch (e) {
      message.error(
        i18n.t(
          'An error occurred while moving to the next step. Please try again later.'
        )
      );
    } finally {
      BusinessModel.setStartLoadingCategoryDataForId(null);
      BusinessModel.setMovingToNextStepLoading(false);
    }
  }
}
